import { Fragment, type ElementType, useEffect, useMemo, useState } from "react";
import {
  ArrowBack,
  DirectionsRun,
  LocalFireDepartment,
  Straighten,
  Favorite,
  AccessTime,
  Bloodtype,
  MonitorHeart,
  Bedtime,
  MonitorWeight,
  Send,
  Close,
  History,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { toast } from "react-toastify";
import {
  clearGoogleHealthcareAccessToken,
  fetchGoogleHealthFitnessBundle,
  GoogleHealthScopeError,
  GOOGLE_HEALTH_SCOPE_RECONNECT_MESSAGE,
  type IGoogleHealthFitnessBundle,
} from "@/services/google/health";
import { RefreshTokenGoogle } from "@/services/google/RefreshTokenGoogle";
import { GoogleHealthcareAuthCard } from "@/pages/profile/components/GoogleHealthcareAuthCard";
import { useSessionContext } from "@/contexts/SessionContext";
import { PageHeader } from "@/components/common/PageHeader";
import { ScreenMobile } from "@/components/common/ScreenMobile";
import { FitnessDataHistoryPanel } from "./FitnessDataHistoryPanel";
import { FitnessModalDateRow } from "./FitnessModalDateRow";
import { FitnessDataSharePanel } from "./FitnessDataSharePanel";
import {
  formatarDadosGoogleHealth,
  formatDistanceFromMeters,
  formatSleepDurationFromMinutes,
  formatWeightFromKg,
  hasGoogleHealthMetrics,
} from "@/utils/wearableFitFormat";

interface FitnessDataModalProps {
  open: boolean;
  onClose: () => void;
  refreshToken: string;
}

type MetricKey =
  | "steps"
  | "calories"
  | "distance"
  | "heartRate"
  | "activeMinutes"
  | "glucose"
  | "bloodPressure"
  | "weight"
  | "sleep";

const EMPTY_FITNESS_BUNDLE: IGoogleHealthFitnessBundle = {
  steps: [],
  distance: [],
  sleep: [],
  electrocardiogram: [],
  heartRate: [],
  weight: [],
  queriedAt: "",
};

const METRIC_CONFIG: Array<{
  key: MetricKey;
  label: string;
  icon: ElementType;
  color: string;
}> = [
    { key: "steps", label: "Passos", icon: DirectionsRun, color: "#1E88E5" },
    { key: "calories", label: "Gasto de energia", icon: LocalFireDepartment, color: "#F4511E" },
    { key: "distance", label: "Distância", icon: Straighten, color: "#7B1FA2" },
    { key: "heartRate", label: "Frequência cardíaca", icon: Favorite, color: "#E53935" },
    { key: "weight", label: "Peso", icon: MonitorWeight, color: "#455A64" },
    { key: "activeMinutes", label: "Minutos em movimento", icon: AccessTime, color: "#00897B" },
    { key: "glucose", label: "Glicose", icon: Bloodtype, color: "#3949AB" },
    { key: "bloodPressure", label: "Pressão arterial", icon: MonitorHeart, color: "#6D4C41" },
    { key: "sleep", label: "Sono", icon: Bedtime, color: "#5E35B1" },
  ];

export function FitnessDataModal({
  open,
  onClose,
  refreshToken,
}: FitnessDataModalProps) {
  const theme = useTheme();
  /** Em telas estreitas o Dialog ocupa a viewport inteira para evitar cortes e scroll horizontal. */
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useSessionContext();
  const isConnected = Boolean(user?.refreshTokenGoogleFit);
  const today = dayjs().startOf("day");
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [loading, setLoading] = useState(false);
  const [fitnessBundle, setFitnessBundle] = useState<IGoogleHealthFitnessBundle | null>(
    null
  );
  /** Troca o conteúdo do mesmo modal (dados Google Fit, envio ao prontuário ou histórico FHIR). */
  const [modalView, setModalView] = useState<"data" | "share" | "history">(
    "data"
  );
  /** Carregamento do GetObservation no painel de histórico (navegação de data bloqueada enquanto busca). */
  const [historyLoading, setHistoryLoading] = useState(false);
  /** Skeleton ao trocar o dia no histórico (evita nova troca até o feedback terminar). */
  const [historyDayTransition, setHistoryDayTransition] = useState(false);
  const googleHealthcareClientId =
    import.meta.env.VITE_GOOGLE_HEALTHCARE_CLIENT_ID ||
    import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const googleHealthcareClientSecret =
    import.meta.env.VITE_GOOGLE_HEALTHCARE_CLIENT_SECRET ||
    import.meta.env.VITE_GOOGLE_CLIENT_SECRET;

  const parsedMetrics = useMemo(() => {
    const fitData = formatarDadosGoogleHealth(fitnessBundle, selectedDate);
    return METRIC_CONFIG.map((metric) => ({
      ...metric,
      value:
        metric.key === "steps"
          ? String(Math.round(Number(fitData.passos || 0)))
          : metric.key === "calories"
            ? Number(fitData.calorias || 0).toFixed(1)
            : metric.key === "distance"
              ? formatDistanceFromMeters(Number(fitData.distanciaMetros || 0))
              : metric.key === "heartRate"
                ? `${Math.round(Number(fitData.bpmMedio || 0))} bpm`
                : metric.key === "weight"
                  ? formatWeightFromKg(Number(fitData.pesoKg || 0))
                  : metric.key === "activeMinutes"
                    ? String(Math.round(Number(fitData.minutosAtivos || 0)))
                    : metric.key === "glucose"
                      ? Number(fitData.glicose || 0).toFixed(1)
                      : metric.key === "bloodPressure"
                        ? fitData.pressao
                        : formatSleepDurationFromMinutes(Number(fitData.sonoMinutos || 0)),
    }));
  }, [fitnessBundle, selectedDate]);

  useEffect(() => {
    if (!isConnected) {
      setFitnessBundle(null);
    }
  }, [isConnected]);

  useEffect(() => {
    if (!open) {
      setModalView("data");
    }
  }, [open]);

  const handleSearch = async (dateOverride?: Dayjs) => {
    if (!refreshToken) {
      toast.error("Conecte sua conta Google Fitness no perfil para consultar dados.");
      return;
    }
    const dateToSearch = dateOverride || selectedDate;

    setLoading(true);
    try {
      let accessTokenGoogle = localStorage.getItem("googleHealthcareAccessToken") || "";

      if (!accessTokenGoogle) {
        const tokenData = await RefreshTokenGoogle(refreshToken, {
          clientId: googleHealthcareClientId,
          clientSecret: googleHealthcareClientSecret,
        });
        accessTokenGoogle = tokenData.access_token;
        localStorage.setItem("googleHealthcareAccessToken", accessTokenGoogle);
      }

      const { bundle, errors } = await fetchGoogleHealthFitnessBundle({
        selectedDate: dateToSearch,
        accessTokenGoogle,
        refreshToken,
        setAccessTokenGoogle: (value: string) =>
          localStorage.setItem("googleHealthcareAccessToken", value),
        googleClientId: googleHealthcareClientId,
        googleClientSecret: googleHealthcareClientSecret,
      });

      setFitnessBundle(bundle);

      if (errors.length > 0) {
        toast.warn(
          `Alguns dados não puderam ser carregados (${errors.map((e) => e.dataType).join(", ")}).`
        );
      }
    } catch (error) {
      console.error("Erro ao buscar dados fitness:", error);

      if (error instanceof GoogleHealthScopeError) {
        clearGoogleHealthcareAccessToken();
        setFitnessBundle(EMPTY_FITNESS_BUNDLE);
        toast.error(GOOGLE_HEALTH_SCOPE_RECONNECT_MESSAGE, { autoClose: 8000 });
        return;
      }

      setFitnessBundle(EMPTY_FITNESS_BUNDLE);
      toast.error("Não foi possível consultar os dados fitness.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeDay = (deltaDays: number) => {
    if (modalView === "data" && loading) return;
    if (modalView === "history" && (historyLoading || historyDayTransition)) return;
    const candidateDate = selectedDate.add(deltaDays, "day");
    const newDate = candidateDate.isAfter(today, "day") ? today : candidateDate;
    setSelectedDate(newDate);
    // Só consulta o Google Fit na visão principal; no histórico a lista filtra pelo dia localmente após o GET inicial.
    if (open && isConnected && refreshToken && modalView === "data") {
      handleSearch(newDate);
    }
  };

  /** Data escolhida no calendário (área central); limita até hoje e dispara busca só na visão de dados. */
  const handlePickDate = (picked: Dayjs) => {
    if (modalView === "data" && loading) return;
    if (modalView === "history" && (historyLoading || historyDayTransition)) return;
    const clamped = picked.isAfter(today, "day") ? today : picked.startOf("day");
    setSelectedDate(clamped);
    if (open && isConnected && refreshToken && modalView === "data") {
      handleSearch(clamped);
    }
  };

  const handleGoToToday = () => {
    if (modalView === "data" && loading) return;
    if (modalView === "history" && (historyLoading || historyDayTransition)) return;

    const todayStart = today.startOf("day");
    setSelectedDate(todayStart);
    if (open && isConnected && refreshToken && modalView === "data") {
      handleSearch(todayStart);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (!isConnected) return;
    if (!refreshToken) return;
    // Carrega automaticamente os dados ao abrir o modal pela primeira vez.
    handleSearch(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isConnected, refreshToken]);

  /** Painel de envio ao prontuário (reutilizado no Dialog desktop e no ScreenMobile). */
  const sharePanel = (
    <FitnessDataSharePanel
      fitnessBundle={fitnessBundle}
      selectedDate={selectedDate}
      onBack={() => setModalView("data")}
      onCloseModal={onClose}
    />
  );

  /** Observações FHIR enviadas (GetObservation) com agrupamento por horário de envio. */
  const historyPanel = (
    <FitnessDataHistoryPanel
      selectedDate={selectedDate}
      today={today}
      patientUsername={user?.username}
      onLoadingChange={setHistoryLoading}
      onDateTransitionChange={setHistoryDayTransition}
      onPrevDay={() => handleChangeDay(-1)}
      onNextDay={() => handleChangeDay(1)}
      onPickDate={handlePickDate}
    />
  );

  /** No mobile o título vai no ScreenMobile; descrição + termos entram no corpo (como nas outras telas). */
  const mobileDataIntro = (
    <>
      <Typography variant="body2" color="text.secondary">
        Acompanhe e compartilhe dados do seu Google Fit
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Link
          href="https://fluxmed.com.br/politica-de-privacidade/"
          target="_blank"
          rel="noreferrer"
          underline="none"
        >
          <Chip
            label="Política de privacidade"
            size="small"
            clickable
            variant="outlined"
            sx={{
              fontWeight: 600,
              color: "primary800.main",
              borderColor: "primary300.main",
              bgcolor: (theme) => alpha(theme.palette.primary100.main, 0.7),
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary100.main, 1),
                borderColor: "primary500.main",
              },
            }}
          />
        </Link>
      </Box>
    </>
  );

  const dataViewBody = (
    <Stack spacing={2} sx={{ pt: 1 }}>
      {isXs ? mobileDataIntro : null}
      <GoogleHealthcareAuthCard compact showRevokeDialog={false} />

      {isConnected && (
        <>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              backgroundColor: "primary800.main",
              color: "white",
              borderRadius: 2,
            }}
          >
            <Stack spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
              {/* Linha 1: data + atalho para hoje. */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: "100%",
                    maxWidth: { xs: "100%", sm: 720 },
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, maxWidth: 640 }}>
                    <FitnessModalDateRow
                      fullWidth
                      selectedDate={selectedDate}
                      today={today}
                      loading={loading}
                      onPrevDay={() => handleChangeDay(-1)}
                      onNextDay={() => handleChangeDay(1)}
                      onPickDate={handlePickDate}
                    />
                  </Box>
                  <Tooltip
                    title={
                      selectedDate.isSame(today, "day")
                        ? "Você já está em hoje"
                        : "Ir para hoje"
                    }
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        flexShrink: 0,
                        alignSelf: "center",
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={
                          loading ||
                          selectedDate.isSame(today, "day") ||
                          (modalView === "history" &&
                            (historyLoading || historyDayTransition))
                        }
                        onClick={handleGoToToday}
                        aria-label="Ir para hoje"
                        sx={{
                          minWidth: 56,
                          px: 1.25,
                          py: 0.5,
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "0.8125rem",
                          lineHeight: 1.2,
                          color: "white",
                          borderColor: (theme) =>
                            alpha(theme.palette.neutral0.main, 0.35),
                          bgcolor: (theme) =>
                            alpha(theme.palette.neutral0.main, 0.12),
                          "&:hover": {
                            borderColor: (theme) =>
                              alpha(theme.palette.neutral0.main, 0.5),
                            bgcolor: (theme) =>
                              alpha(theme.palette.neutral0.main, 0.2),
                          },
                          "&.Mui-disabled": {
                            color: (theme) =>
                              alpha(theme.palette.neutral0.main, 0.3),
                            borderColor: (theme) =>
                              alpha(theme.palette.neutral0.main, 0.15),
                            bgcolor: (theme) =>
                              alpha(theme.palette.neutral0.main, 0.06),
                          },
                        }}
                      >
                        Hoje
                      </Button>
                    </Box>
                  </Tooltip>
                </Stack>
              </Box>

              {/* Linha 2: histórico e envio ao prontuário — centralizados. */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems="center"
                justifyContent="center"
                flexWrap="wrap"
                sx={{ width: "100%", maxWidth: 720 }}
              >
                <Button
                  variant="outlined"
                  startIcon={<History />}
                  onClick={() => setModalView("history")}
                  disabled={loading || !user?.username}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: 200 },
                    textTransform: "none",
                    fontWeight: 600,
                    color: "white",
                    borderColor: (theme) => alpha(theme.palette.neutral0.main, 0.35),
                    bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.12),
                    "&:hover": {
                      borderColor: (theme) => alpha(theme.palette.neutral0.main, 0.5),
                      bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.2),
                    },
                    "&.Mui-disabled": {
                      color: (theme) => alpha(theme.palette.neutral0.main, 0.3),
                      borderColor: (theme) => alpha(theme.palette.neutral0.main, 0.15),
                      bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.06),
                    },
                  }}
                >
                  Histórico de envio
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  onClick={() => setModalView("share")}
                  disabled={loading || !fitnessBundle}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: 200 },
                    textTransform: "none",
                    fontWeight: 600,
                    bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.22),
                    color: "white",
                    "&:hover": {
                      bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.32),
                    },
                  }}
                >
                  Enviar ao prontuário
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Stack spacing={1.5}>
            {loading ? (
              <Grid container spacing={1.5}>
                {Array.from({ length: 9 }).map((_, index) => (
                  <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "grey.200",
                        minHeight: 120,
                        background:
                          "linear-gradient(145deg, rgba(255,255,255,1) 0%, rgba(248,249,252,1) 100%)",
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Skeleton variant="text" width="55%" height={22} />
                          <Skeleton variant="circular" width={34} height={34} />
                        </Box>
                        <Skeleton variant="text" width="70%" height={34} />
                      </Stack>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : fitnessBundle ? (
              <>
                {!hasGoogleHealthMetrics(fitnessBundle, selectedDate) && (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum dado retornado para este dia.
                  </Typography>
                )}
                <Grid container spacing={1.5}>
                  {parsedMetrics.map((metric) => (
                    <Grid item xs={12} sm={6} md={4} key={metric.key}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "grey.200",
                          minHeight: 120,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          background:
                            "linear-gradient(145deg, rgba(255,255,255,1) 0%, rgba(248,249,252,1) 100%)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="subtitle2">{metric.label}</Typography>
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: "50%",
                              bgcolor: `${metric.color}20`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <metric.icon sx={{ color: metric.color, fontSize: 18 }} />
                          </Box>
                        </Box>
                        <Typography
                          variant="h6"
                          color="text.primary"
                          sx={{
                            mt: 1,
                            fontWeight: 700,
                            fontSize: { xs: "1rem", sm: "1.25rem" },
                            wordBreak: "break-word",
                          }}
                        >
                          {metric.value}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </>
            ) : null}
          </Stack>
        </>
      )}

      {!isConnected && (
        <Typography variant="body2" color="text.secondary">
          Vincule sua conta Google para visualizar seus dados.
        </Typography>
      )}
    </Stack>
  );

  return (
    <Fragment>
      {isXs ? (
        <ScreenMobile
          open={open}
          onClose={onClose}
          title={
            modalView === "share"
              ? "Enviar dados ao prontuário"
              : modalView === "history"
                ? "Histórico de envio"
                : "Dados fitness"
          }
          onBack={
            modalView === "share" || modalView === "history"
              ? () => setModalView("data")
              : undefined
          }
        >
          {modalView === "share"
            ? sharePanel
            : modalView === "history"
              ? historyPanel
              : dataViewBody}
        </ScreenMobile>
      ) : (
        <Dialog
          open={open}
          onClose={onClose}
          fullWidth
          maxWidth="md"
          scroll="paper"
        >
          <DialogTitle sx={{ p: { xs: 1, sm: 1.5 } }}>
            {modalView === "share" || modalView === "history" ? (
              <Box sx={{ position: "relative", pr: { xs: 5, sm: 5.5 } }}>
                <IconButton
                  onClick={onClose}
                  size="small"
                  aria-label="Fechar"
                  sx={{
                    position: "absolute",
                    top: { xs: 4, sm: 8 },
                    right: { xs: 0, sm: 4 },
                    zIndex: 1,
                    color: "neutral600.main",
                    bgcolor: (t) => alpha(t.palette.neutral300.main, 0.3),
                    "&:hover": {
                      bgcolor: (t) => alpha(t.palette.neutral300.main, 0.45),
                    },
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ minWidth: 0, pr: 0.5 }}
                >
                  <IconButton
                    onClick={() => setModalView("data")}
                    size="small"
                    aria-label="Voltar aos dados fitness"
                    sx={{
                      flexShrink: 0,
                      color: "primary800.main",
                      bgcolor: (theme) => alpha(theme.palette.neutral300.main, 0.35),
                      "&:hover": {
                        bgcolor: (theme) => alpha(theme.palette.neutral300.main, 0.5),
                      },
                    }}
                  >
                    <ArrowBack fontSize="small" />
                  </IconButton>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "1rem", sm: "1.25rem" },
                      lineHeight: 1.25,
                      overflow: "hidden",
                      textOverflow: { xs: "unset", sm: "ellipsis" },
                      display: "-webkit-box",
                      WebkitLineClamp: { xs: 2, sm: 1 },
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {modalView === "share"
                      ? "Enviar dados ao prontuário"
                      : "Histórico de envio de dados"}
                  </Typography>
                </Stack>
              </Box>
            ) : (
              /**
               * Uma linha: título à esquerda; chip + fechar agrupados à direita (sem segunda faixa vazia).
               */
              <Stack
                direction="row"
                alignItems="flex-start"
                sx={{ width: "100%", gap: { xs: 1, sm: 1.5 } }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <PageHeader
                    title="Dados fitness"
                    description="Acompanhe e compartilhe dados do seu Google Fit"
                    icon={MonitorHeart}
                  />
                </Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{
                    flexShrink: 0,
                    /* Alinha com a primeira linha do PageHeader (ícone + título). */
                    mt: { xs: 1, sm: 1.25 },
                  }}
                >
                  <Link
                    href="https://fluxmed.com.br/politica-de-privacidade/"
                    target="_blank"
                    rel="noreferrer"
                    underline="none"
                  >
                    <Chip
                      label="Política de privacidade"
                      size="small"
                      clickable
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        color: "primary800.main",
                        borderColor: "primary300.main",
                        bgcolor: (theme) => alpha(theme.palette.primary100.main, 0.7),
                        "&:hover": {
                          bgcolor: (theme) => alpha(theme.palette.primary100.main, 1),
                          borderColor: "primary500.main",
                        },
                      }}
                    />
                  </Link>
                  <IconButton
                    onClick={onClose}
                    size="small"
                    aria-label="Fechar"
                    sx={{
                      color: "neutral600.main",
                      bgcolor: (t) => alpha(t.palette.neutral300.main, 0.3),
                      "&:hover": {
                        bgcolor: (t) => alpha(t.palette.neutral300.main, 0.45),
                      },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            )}
          </DialogTitle>
          <DialogContent sx={{ px: { xs: 1, sm: 2 }, pb: 2 }}>
            {modalView === "share"
              ? sharePanel
              : modalView === "history"
                ? historyPanel
                : dataViewBody}
          </DialogContent>
          <DialogActions />
        </Dialog>
      )}
    </Fragment>
  );
}
